import {
  AlignLeft01,
  AlignRight01,
  Settings02,
} from "@untitled-ui/icons-react";
import { compact } from "lodash";
import React from "react";
import { useLocation } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  leftSidePanelVar,
  successNotificationVar,
} from "@cache/cache";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import client from "@graphql/client";
import { chromeExtensionUrl } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { toWithBackground } from "@helpers/helpers";

import { apolloPersistCacheKey } from "../../../helpers/constants";
import { isInternalImpersonation } from "./sidebar-hijack-helpers";
import { sidebarButtonClassName } from "./sidebar-navigation";

type Props = {
  menuIsCollapsed?: boolean;
  onToggleMenu?: () => void;
};

const SidebarSettings: React.FC<Props> = ({
  menuIsCollapsed,
  onToggleMenu,
}) => {
  const location = useLocation<TFLocationState>();
  const organization = currentOrganizationVar();
  const { saveUiPreference } = useUiPreferenceCache();
  const link = useLink();

  const clearCache = () => {
    client.resetStore();
    client.clearStore();
    localStorage.removeItem(apolloPersistCacheKey);
  };

  const handleClearCache = () => {
    clearCache();
    successNotificationVar({ title: "Cache cleared" });
  };

  const handleLogout = () => {
    clearCache();
    window.location.href = "/logout";
    if (window.Intercom) {
      window.Intercom("shutdown");
    }
  };

  const openSettingsAndNavigate = (path: string) => {
    saveUiPreference({
      leftNavigationSidebarExpanded: true,
    });
    leftSidePanelVar("settings");
    link.redirect(path);
  };

  const handleOpenSettings = () => {
    openSettingsAndNavigate("/account/profile");
  };

  const options = compact([
    {
      type: "link",
      label: "Invite People",
      to: toWithBackground({
        pathname: "/invite",
        location,
      }),
    },
    {
      type: "button",
      label: "Org Chart",
      onClick: () => openSettingsAndNavigate("/org-chart"),
    },
    {
      type: "divider",
    },
    {
      type: "button",
      label: "Settings",
      onClick: handleOpenSettings,
    },
    {
      type: "divider",
    },
    organization.featureFlags.meetings && {
      type: "button",
      label: "Meeting Templates",
      onClick: () => openSettingsAndNavigate("/templates"),
    },
    organization.featureFlags.meetings && {
      type: "link",
      label: "Get the Chrome Extension",
      to: chromeExtensionUrl,
    },
    {
      type: "button",
      label: "Clear Cache",
      onClick: handleClearCache,
    },
    {
      type: "divider",
    },
    {
      type: "button",
      label: "Logout",
      onClick: handleLogout,
    },
  ]) as DropdownOptionType[];

  return (
    <div
      className={classNames(
        "flex px-3 py-2 w-full justify-between",
        menuIsCollapsed ? "flex-col items-center gap-2" : "gap-1",
      )}
    >
      <div className="flex items-center flex-1">
        <Dropdown options={options}>
          <button
            aria-label="Sidebar settings"
            className={classNames(
              "flex cursor-pointer flex-1 items-center min-w-0 text-left gap-1.5 text-gray-700 text-sm hover:bg-nav-item-active group",
              menuIsCollapsed ? "justify-center w-full" : "justify-start",
              sidebarButtonClassName,
            )}
          >
            <Settings02
              className={classNames("shrink-0 text-gray-500", "h-4 w-4")}
            />
            {!menuIsCollapsed && "Settings"}
          </button>
        </Dropdown>
      </div>
      <div className="flex items-center">
        {window.hijackedUser && isInternalImpersonation() && (
          <form action={window.hijackedUser.release_url} method="POST">
            <input
              type="hidden"
              name="csrfmiddlewaretoken"
              value={window.TF.csrf}
            />
            <input
              type="hidden"
              name="next"
              value={window.hijackedUser.next_url}
            />
            <Tooltip
              placement="right"
              text={`Release ${window.hijackedUser.user.name}`}
            >
              <button
                type="submit"
                aria-label={`Release ${window.hijackedUser.user.name}`}
                className={classNames(
                  "cursor-pointer flex items-center justify-center text-gray-500 text-sm hover:bg-nav-item-active hover:text-gray-600 shrink-0",
                  sidebarButtonClassName,
                )}
              >
                <TopicflowIcon
                  type="exit"
                  className={classNames("h-4 w-4", "shrink-0")}
                />
              </button>
            </Tooltip>
          </form>
        )}
        <Tooltip
          placement="right"
          text={menuIsCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <button
            onClick={onToggleMenu}
            aria-label="Toggle sidebar button"
            className={classNames(
              "cursor-pointer hidden sm:flex items-center justify-center text-gray-500 text-sm hover:bg-nav-item-active hover:text-gray-600 shrink-0",
              sidebarButtonClassName,
            )}
          >
            {menuIsCollapsed ? (
              <AlignRight01 className={classNames("h-4 w-4", "shrink-0")} />
            ) : (
              <AlignLeft01 className={classNames("h-4 w-4", "shrink-0")} />
            )}
          </button>
        </Tooltip>
      </div>
    </div>
  );
};
export default SidebarSettings;
